import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  // Container,
  Tabs,
  Tab,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Paper,
  useTheme,
} from '@mui/material';
import { useAuth } from '@/hooks';
import {
  followService,
  FollowerFollowingUser,
  FollowRequestItem,
} from '@/services/follow/followService';
import { ProfileCard } from '@/components/common/ProfileCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`network-tabpanel-${index}`}
      aria-labelledby={`network-tab-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

export const NetworkPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = useMemo(() => {
    const tabParam = searchParams.get('tab');
    return tabParam ? parseInt(tabParam, 10) : 0;
  }, []);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [followers, setFollowers] = useState<FollowerFollowingUser[]>([]);
  const [following, setFollowing] = useState<FollowerFollowingUser[]>([]);
  const [sentRequests, setSentRequests] = useState<FollowRequestItem[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<FollowRequestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [followersPage, setFollowersPage] = useState(1);
  const [followingPage, setFollowingPage] = useState(1);
  const [sentPage, setSentPage] = useState(1);
  const [receivedPage, setReceivedPage] = useState(1);
  const [hasMoreFollowers, setHasMoreFollowers] = useState(true);
  const [hasMoreFollowing, setHasMoreFollowing] = useState(true);
  const [hasMoreSent, setHasMoreSent] = useState(true);
  const [hasMoreReceived, setHasMoreReceived] = useState(true);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchParams(newValue === 0 ? {} : { tab: newValue.toString() });
  };

  const loadFollowers = async (page = 1, reset = false) => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const response = await followService.getFollowers(user.id, {
        page,
        limit: 12,
      });

      console.log('Followers response:', response);

      if (response.success) {
        if (reset || page === 1) {
          setFollowers(response.data.followers);
        } else {
          setFollowers((prev) => [...prev, ...response.data.followers]);
        }
        setHasMoreFollowers(page < response.data.pagination.pages);
        setFollowersPage(page);
      }
    } catch (error) {
      console.error('Failed to load followers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowing = async (page = 1, reset = false) => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const response = await followService.getFollowing(user.id, {
        page,
        limit: 12,
      });

      console.log('Following response:', response);

      if (response.success) {
        if (reset || page === 1) {
          setFollowing(response.data.following);
        } else {
          setFollowing((prev) => [...prev, ...response.data.following]);
        }
        setHasMoreFollowing(page < response.data.pagination.pages);
        setFollowingPage(page);
      }
    } catch (error) {
      console.error('Failed to load following:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSentRequests = async (page = 1, reset = false) => {
    try {
      setLoading(true);
      const response = await followService.getFollowRequests({
        type: 'sent',
        page,
        limit: 12,
      });

      console.log('Sent requests response:', response);

      if (response.success) {
        if (reset || page === 1) {
          setSentRequests(response.data.requests);
        } else {
          setSentRequests((prev) => [...prev, ...response.data.requests]);
        }
        setHasMoreSent(page < response.data.pagination.pages);
        setSentPage(page);
      }
    } catch (error) {
      console.error('Failed to load sent requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReceivedRequests = async (page = 1, reset = false) => {
    try {
      setLoading(true);
      const response = await followService.getFollowRequests({
        type: 'received',
        page,
        limit: 12,
      });

      console.log('Received requests response:', response);

      if (response.success) {
        if (reset || page === 1) {
          setReceivedRequests(response.data.requests);
        } else {
          setReceivedRequests((prev) => [...prev, ...response.data.requests]);
        }
        setHasMoreReceived(page < response.data.pagination.pages);
        setReceivedPage(page);
      }
    } catch (error) {
      console.error('Failed to load received requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      await followService.respondToRequest(requestId, { response: 'accepted' });
      loadReceivedRequests(1, true);
    } catch (error) {
      console.error('Failed to accept request:', error);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await followService.respondToRequest(requestId, { response: 'rejected' });
      loadReceivedRequests(1, true);
    } catch (error) {
      console.error('Failed to reject request:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 0) {
      loadFollowers(1, true);
    } else if (activeTab === 1) {
      loadFollowing(1, true);
    } else if (activeTab === 2) {
      loadSentRequests(1, true);
    } else if (activeTab === 3) {
      loadReceivedRequests(1, true);
    }
  }, [activeTab, user?.id]);

  useEffect(() => {
      // loadFollowers(1, true);
      loadFollowing(1, true);
      loadSentRequests(1, true);
      loadReceivedRequests(1, true);
  }, []);

  const convertToUserSearchResult = (user: FollowerFollowingUser) => {
    console.log('Converting user:', user);
    return {
      ...user,
      whatsappNumber: '',
      isFollowing: user.status === 'following',
      hasRequestPending: user.isRequestPending || false,
    };
  };

  const convertRequestToUserSearchResult = (request: FollowRequestItem, isReceived: boolean) => {
    const userInfo = isReceived ? request.requester : (request.recipient as any);
    return {
      _id: userInfo._id,
      name: userInfo.name || userInfo.email,
      email: userInfo.aboutMe || userInfo.email,
      whatsappNumber: '',
      profilePictureUrl: userInfo.profilePictureUrl || null,
      isFollowing: false,
      hasRequestPending: true,
    };
  };

  return (
    <Box maxWidth="xl" sx={{ pb: 4 ,pl:0 , pr:0}}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            My Network
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="network tabs">
            <Tab label={`Followers (${followers.length})`} />
            <Tab label={`Following (${following.length})`} />
            <Tab label={`Invite Sent (${sentRequests.length})`} />
            <Tab label={`Invite Received (${receivedRequests.length})`} />
                {/* <Tab label={`Followers `} />
            <Tab label={`Following `} />
            <Tab label={`Invite Sent `} />
            <Tab label={`Invite Received `} /> */}
          </Tabs>
        </Box>

        <Box sx={{ px: 3, minHeight: '400px' }}>
          {/* Followers Tab */}
          <TabPanel value={activeTab} index={0}>
            {loading && followers.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : followers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No followers yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When people follow you, they will appear here
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {followers.map((follower) => {
                    console.log('Rendering follower:', follower);
                    return (
                      <Grid size={{xs: 12, sm: 6, md: 4}} key={follower._id}>
                        <ProfileCard
                          user={convertToUserSearchResult(follower)}
                          variant="search"
                        refresh={loadFollowers}

                        />
                      </Grid>
                    );
                  })}
                </Grid>
                {hasMoreFollowers && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 3 }}>
                    <Button
                      onClick={() => loadFollowers(followersPage + 1)}
                      disabled={loading}
                      variant="outlined"
                      size="large"
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </TabPanel>

          {/* Following Tab */}
          <TabPanel value={activeTab} index={1}>
            {loading && following.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : following.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Not following anyone yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start following people to see them here
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {following.map((followingUser) => {
                    console.log('Rendering following user:', followingUser);
                    return (
                      <Grid size={{xs: 12, sm: 6, md: 4}} key={followingUser._id}>
                        <ProfileCard
                          user={convertToUserSearchResult(followingUser)}
                          variant="search"
                        refresh={loadFollowing}

                        />
                      </Grid>
                    );
                  })}
                </Grid>
                {hasMoreFollowing && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 3 }}>
                    <Button
                      onClick={() => loadFollowing(followingPage + 1)}
                      disabled={loading}
                      variant="outlined"
                      size="large"
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </TabPanel>

          {/* Sent Requests Tab */}
          <TabPanel value={activeTab} index={2}>
            {loading && sentRequests.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : sentRequests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No sent requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your sent follow requests will appear here
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {sentRequests.map((request) => (
                    <Grid size={{xs: 12, sm: 6, md: 4}} key={request._id}>
                      <ProfileCard
                        user={convertRequestToUserSearchResult(request, false)}
                        variant="search"
                        refresh={loadSentRequests}
                      />
                    </Grid>
                  ))}
                </Grid>
                {hasMoreSent && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 3 }}>
                    <Button
                      onClick={() => loadSentRequests(sentPage + 1)}
                      disabled={loading}
                      variant="outlined"
                      size="large"
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </TabPanel>

          {/* Received Requests Tab */}
          <TabPanel value={activeTab} index={3}>
            {loading && receivedRequests.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : receivedRequests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No received requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Follow requests from others will appear here
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {receivedRequests.map((request) => (
                    <Grid size={{xs: 12, sm: 6, md: 4}} key={request._id}>
                      <ProfileCard
                        user={convertRequestToUserSearchResult(request, true)}
                        variant="search"
                        onAccept={() => handleAcceptRequest(request._id)}
                        onReject={() => handleRejectRequest(request._id)}
                        showAcceptReject={true}
                        refresh={loadReceivedRequests}

                      />
                    </Grid>
                  ))}
                </Grid>
                {hasMoreReceived && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pb: 3 }}>
                    <Button
                      onClick={() => loadReceivedRequests(receivedPage + 1)}
                      disabled={loading}
                      variant="outlined"
                      size="large"
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};
